import { type ReactNode, useState } from "react";
import { ExternalLink, Package } from "lucide-react";

import { imageUrl } from "@/features/imageUpload";
import {
  acceptReservationOffer,
  counterReservationOffer,
  declineReservationOffer,
  isReservationItemUnavailable,
  type ReservationOfferView,
  type ReservationStatus,
} from "@/features/storefront";

// TNK-167 — shared reservation-detail renderer. Used by both the public
// /shop/r/:token status page and the account-scoped
// /shop/account/reservations/:id page. The two differ only in how they LOAD
// the reservation (public token vs buyer-gated id) and their header/footer
// chrome; the status pill, offer panel, accept/decline plumbing, cards list,
// and totals are identical, so they live here. Offer actions go through the
// token-based endpoints, so callers pass the reservation's `token`.
const STATUS_STYLE: Record<string, string> = {
  Pending: "bg-warning text-warning-foreground",
  Accepted: "bg-success text-success-foreground",
  Declined: "bg-destructive/15 text-destructive",
  Expired: "bg-muted text-muted-foreground",
  Completed: "bg-success text-success-foreground",
  // TNK-127 phase 2 lifecycle states.
  UnderReview: "bg-secondary text-secondary-foreground",
  OfferSent: "bg-warning text-warning-foreground",
  AwaitingPayment: "bg-primary text-primary-foreground",
  Paid: "bg-success text-success-foreground",
  Cancelled: "bg-destructive/15 text-destructive",
};

export function ReservationDetailView({
  reservation,
  token,
  onReservationUpdate,
  title = "Your reservation",
  subtitle,
  footer,
}: {
  reservation: ReservationStatus;
  /** Status token used for offer accept/decline (token-based endpoints). */
  token: string;
  /** Called with the patched reservation after an accept/decline so the
   *  owning route can keep its state in sync without a refetch. */
  onReservationUpdate: (next: ReservationStatus) => void;
  title?: string;
  subtitle?: ReactNode;
  footer?: ReactNode;
}) {
  const [actBusy, setActBusy] = useState(false);
  const [actError, setActError] = useState("");

  const actOnOffer = async (
    action: "accept" | "decline",
    offerId: string,
  ): Promise<void> => {
    setActBusy(true);
    setActError("");
    try {
      const result =
        action === "accept"
          ? await acceptReservationOffer(token, offerId)
          : await declineReservationOffer(token, offerId);
      onReservationUpdate({
        ...reservation,
        status: result.reservationStatus,
        currentOffer: result.offer,
      });
    } catch (e) {
      setActError(e instanceof Error ? e.message : "Action failed.");
    } finally {
      setActBusy(false);
    }
  };

  const counterOffer = async (
    offerId: string,
    total: number,
  ): Promise<void> => {
    setActBusy(true);
    setActError("");
    try {
      const result = await counterReservationOffer(token, offerId, total);
      onReservationUpdate({
        ...reservation,
        status: result.reservationStatus,
        currentOffer: result.offer,
        counterAttemptsRemaining: result.countersRemaining,
      });
    } catch (e) {
      setActError(e instanceof Error ? e.message : "Counter failed.");
    } finally {
      setActBusy(false);
    }
  };

  const r = reservation;
  // TNK-127 phase 1 — total reflects what the buyer agreed to at submit time
  // (`requestedPrice` snapshot), falling back to current market for legacy
  // reservations. TNK-163 — seller-removed items don't count toward the total.
  const total = r.cards.reduce((s, c) => {
    if (c.itemStatus === "removed_unavailable" || c.itemStatus === "released") {
      return s;
    }
    return s + (c.requestedPrice ?? c.currentMarketPrice ?? 0);
  }, 0);

  return (
    <div className="mx-auto max-w-lg space-y-4">
      <div className="space-y-1">
        <h1 className="shop-display text-2xl font-bold text-foreground">
          {title}
        </h1>
        {subtitle && (
          <p className="text-sm text-muted-foreground">{subtitle}</p>
        )}
      </div>

      <div className="flex items-center gap-2">
        <span
          className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
            STATUS_STYLE[r.status] ?? "bg-muted text-muted-foreground"
          }`}
        >
          {r.status}
        </span>
        {r.status === "Pending" && (
          <span className="text-[11px] text-muted-foreground">
            Held until {new Date(r.expiresAt).toLocaleDateString()}
          </span>
        )}
      </div>

      {r.sellerNote && (
        <div className="rounded-md bg-muted px-3 py-2 text-sm text-foreground">
          <span className="font-medium">Seller note:</span> {r.sellerNote}
        </div>
      )}

      {/* TNK-127 phase 2 — current offer. Prominent CTA above the cards list
          so the buyer sees Accept/Decline right away. */}
      {r.currentOffer && (
        <OfferPanel
          offer={r.currentOffer}
          reservationStatus={r.status}
          busy={actBusy}
          error={actError}
          onAccept={() => void actOnOffer("accept", r.currentOffer!.id)}
          onDecline={() => void actOnOffer("decline", r.currentOffer!.id)}
          onCounter={(total) => void counterOffer(r.currentOffer!.id, total)}
          counterAttemptsRemaining={r.counterAttemptsRemaining}
        />
      )}

      {/* TNK-127 phase 3 — payment hand-off. When the seller has pasted a
          manual payment link, surface a prominent "Pay now" CTA; otherwise
          tell the buyer the seller will follow up. */}
      {r.status === "AwaitingPayment" && (
        <div className="space-y-2">
          {r.paymentLinkUrl && (
            <a
              href={r.paymentLinkUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 rounded-md bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground hover:opacity-90"
            >
              Pay now
              <ExternalLink className="h-4 w-4" />
            </a>
          )}
          <p className="text-[12px] text-muted-foreground">
            {r.paymentLinkUrl
              ? "After paying, your seller will confirm and finalize your order."
              : "Your seller will send payment details shortly."}
          </p>
        </div>
      )}

      {r.status === "Paid" && (
        <div className="rounded-md border border-success/40 bg-success/10 px-3 py-2 text-sm font-medium text-success">
          Payment received — thank you! Your order is being prepared.
        </div>
      )}

      <ul className="divide-y divide-border rounded-md border border-border">
        {r.cards.map((card) => {
          const img = card.imageFrontRef
            ? imageUrl(card.imageFrontRef, "thumb")
            : null;
          // TNK-163 — seller-removed items render dimmed with a strike-through
          // price so the buyer sees what was in their cart vs. what's fulfillable.
          const isRemoved =
            card.itemStatus === "removed_unavailable" ||
            card.itemStatus === "released";
          // TNK-127 — card sold/bundled/held-elsewhere since this reservation
          // (selling never mutates other reservations, so flag it read-side).
          const unavailable = !isRemoved && isReservationItemUnavailable(card);
          const price = (
            card.requestedPrice ??
            card.currentMarketPrice ??
            0
          ).toFixed(2);
          // TNK-212 phase 2b — bundle line item: cover + "Bundle · N cards".
          const isBundle = card.kind === "bundle";
          const subtitle = isBundle
            ? `Bundle · ${card.cardCount ?? "?"} card${card.cardCount === 1 ? "" : "s"}`
            : [card.setName, card.condition].filter(Boolean).join(" · ");
          return (
            <li
              key={card.id}
              className={`flex items-center gap-3 p-3 ${isRemoved || unavailable ? "opacity-60" : ""}`}
            >
              <div className="flex h-16 w-12 shrink-0 items-center justify-center overflow-hidden rounded bg-muted text-muted-foreground">
                {img ? (
                  <img
                    src={img}
                    alt={card.name}
                    className={`h-full w-full object-contain ${isRemoved || unavailable ? "grayscale" : ""}`}
                  />
                ) : isBundle ? (
                  <Package size={16} />
                ) : null}
              </div>
              <div className="min-w-0 flex-1">
                <div
                  className={`truncate text-sm font-medium text-foreground ${isRemoved ? "line-through" : ""}`}
                >
                  {card.name}
                </div>
                <div className="truncate text-[11px] text-muted-foreground">
                  {subtitle}
                </div>
                {isRemoved ? (
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-destructive">
                    Seller couldn’t fulfill this
                  </div>
                ) : unavailable ? (
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-warning">
                    No longer available
                  </div>
                ) : null}
              </div>
              <div
                className={`text-sm font-semibold text-foreground ${isRemoved ? "line-through text-muted-foreground" : ""}`}
              >
                ${price}
              </div>
            </li>
          );
        })}
      </ul>

      <div className="flex items-center justify-between border-t border-border pt-3 text-sm">
        <span className="text-muted-foreground">
          {r.cards.length} item{r.cards.length !== 1 ? "s" : ""}
        </span>
        <span
          className="shop-mono text-base font-bold"
          style={{ color: "var(--color-primary)" }}
        >
          ${total.toFixed(2)}
        </span>
      </div>

      {footer}
    </div>
  );
}

// TNK-127 phase 2 — buyer-facing offer panel. Renders the offer breakdown +
// Accept/Decline when the offer is `sent`, or a status banner when it's
// already been actioned. The API serializes expired offers with
// `status: "expired"` even though the DB row stays `sent` until the phase 5
// cleanup — so this component just trusts what it gets back.
function OfferPanel({
  offer,
  reservationStatus,
  busy,
  error,
  onAccept,
  onDecline,
  onCounter,
  counterAttemptsRemaining,
}: {
  offer: ReservationOfferView;
  reservationStatus: string;
  busy: boolean;
  error: string;
  onAccept: () => void;
  onDecline: () => void;
  onCounter: (total: number) => void;
  counterAttemptsRemaining: number;
}) {
  // TNK-127 phase 4 — a `sent` offer is either the seller's (the buyer acts:
  // accept / decline / counter) or the buyer's own counter awaiting the seller.
  const isAdminSent =
    offer.status === "sent" && offer.createdByType === "admin";
  const isBuyerCounterPending =
    offer.status === "sent" && offer.createdByType === "buyer";
  const isAccepted = offer.status === "accepted";
  const [countering, setCountering] = useState(false);
  const [counterValue, setCounterValue] = useState("");
  const canCounter = counterAttemptsRemaining > 0;

  const headerLabel = isAdminSent
    ? "Offer from seller"
    : isBuyerCounterPending
      ? "Your counter — awaiting the seller"
      : isAccepted
        ? "Offer accepted"
        : offer.status === "declined"
          ? "This offer was declined"
          : offer.status === "expired"
            ? "This offer expired"
            : offer.status === "countered"
              ? "Countered"
              : offer.status === "cancelled"
                ? "The seller withdrew this offer"
                : `Offer ${offer.status}`;

  const panelTone =
    isAdminSent || isBuyerCounterPending
      ? "border-warning/40 bg-warning/10"
      : isAccepted
        ? "border-success/40 bg-success/10"
        : "border-border bg-muted/40";

  return (
    <div className={`space-y-3 rounded-md border p-4 ${panelTone}`}>
      <div className="text-sm font-semibold text-foreground">{headerLabel}</div>

      <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-muted-foreground">
        <div>
          Subtotal{" "}
          <span className="font-medium text-foreground">
            ${offer.subtotal.toFixed(2)}
          </span>
        </div>
        <div>
          Discount{" "}
          <span className="font-medium text-foreground">
            ${offer.discount.toFixed(2)}
          </span>
        </div>
        <div>
          Shipping{" "}
          <span className="font-medium text-foreground">
            ${offer.shipping.toFixed(2)}
          </span>
        </div>
        <div>
          Total{" "}
          <span className="text-base font-bold text-foreground">
            ${offer.total.toFixed(2)}
          </span>
        </div>
      </div>

      {offer.message && (
        <p className="rounded bg-background/60 px-3 py-2 text-[12px] italic text-foreground/80">
          “{offer.message}”
        </p>
      )}

      {(isAdminSent || isBuyerCounterPending) && (
        <div className="text-[11px] text-muted-foreground">
          Expires {new Date(offer.expiresAt).toLocaleString()}
        </div>
      )}

      {isBuyerCounterPending && (
        <p className="text-[12px] text-foreground">
          Your counter of ${offer.total.toFixed(2)} is with the seller — we’ll
          email you when they respond.
        </p>
      )}

      {isAccepted && (
        <p className="text-[12px] text-foreground">
          The seller will share payment details shortly.
        </p>
      )}

      {reservationStatus === "Cancelled" && offer.status === "declined" && (
        <p className="text-[12px] text-muted-foreground">
          This reservation is closed.
        </p>
      )}

      {error && <p className="text-[12px] text-destructive">{error}</p>}

      {isAdminSent && !countering && (
        <div className="flex gap-2 pt-1">
          <button
            type="button"
            disabled={busy}
            onClick={onAccept}
            className="inline-flex flex-1 items-center justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            Accept
          </button>
          <button
            type="button"
            disabled={busy || !canCounter}
            onClick={() => {
              setCounterValue(offer.total.toFixed(2));
              setCountering(true);
            }}
            title={
              canCounter
                ? undefined
                : "No counter-offers left on this reservation"
            }
            className="inline-flex flex-1 items-center justify-center rounded-md border border-border bg-card px-3 py-2 text-sm font-semibold text-foreground hover:bg-muted disabled:opacity-50"
          >
            Counter
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={onDecline}
            className="inline-flex flex-1 items-center justify-center rounded-md border border-border bg-card px-3 py-2 text-sm font-semibold text-foreground hover:bg-muted disabled:opacity-50"
          >
            Decline
          </button>
        </div>
      )}

      {isAdminSent && countering && (
        <div className="space-y-2 pt-1">
          <label className="block text-[11px] text-muted-foreground">
            Your counter offer (total)
            <input
              type="number"
              step="0.01"
              min="0"
              value={counterValue}
              onChange={(e) => setCounterValue(e.target.value)}
              placeholder="0.00"
              className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none"
            />
          </label>
          <p className="text-[11px] text-muted-foreground">
            {counterAttemptsRemaining} counter
            {counterAttemptsRemaining === 1 ? "" : "s"} left.
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={busy || !(Number(counterValue) > 0)}
              onClick={() => onCounter(Number(counterValue))}
              className="inline-flex flex-1 items-center justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              Send counter
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => setCountering(false)}
              className="inline-flex flex-1 items-center justify-center rounded-md border border-border bg-card px-3 py-2 text-sm font-semibold text-foreground hover:bg-muted disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
